import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  ShieldCheck, FileWarning, AlertTriangle, CheckCircle2, Circle,
  Users2, Clock, Loader2, RefreshCw, ChevronRight, ChevronDown, Award,
  Trash2, Plus, Save, TrendingUp, Trophy, Signature, ExternalLink, Radar, Receipt,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PMKanbanView } from "../components/pg07/PMKanbanView";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import PG07InvoicesPanel from "../components/pg07/PG07InvoicesPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

// ─── Status pills ─────────────────────────────────────────────────────────
const STATUS_STYLES = {
  ok:    { bg: "bg-emerald-50 dark:bg-emerald-950/30", text: "text-emerald-700 dark:text-emerald-300", ring: "ring-emerald-200/50", icon: CheckCircle2, label: "OK" },
  amber: { bg: "bg-amber-50 dark:bg-amber-950/30",     text: "text-amber-700 dark:text-amber-300",     ring: "ring-amber-200/50",   icon: AlertTriangle, label: "Aviso" },
  red:   { bg: "bg-red-50 dark:bg-red-950/30",         text: "text-red-700 dark:text-red-300",         ring: "ring-red-200/50",     icon: FileWarning, label: "Crítico" },
  sin_evaluar: { bg: "bg-slate-100 dark:bg-slate-800/50", text: "text-slate-600 dark:text-slate-400", ring: "ring-slate-300/50",   icon: Circle,     label: "Sin evaluar" },
  na:    { bg: "bg-slate-50 dark:bg-slate-900/50",     text: "text-slate-500 dark:text-slate-500",     ring: "ring-slate-200/50",   icon: Circle,     label: "No aplica" },
};

function StatusDot({ status, size = "w-3 h-3" }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.na;
  return <span className={`inline-block ${size} rounded-full ${s.bg} ring-1 ${s.ring}`} title={s.label} />;
}

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.na;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${s.bg} ${s.text}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

// ─── KPI Header ───────────────────────────────────────────────────────────
function ComplianceKpis({ counts, total }) {
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);
  const kpis = [
    { key: "ok",    label: "En cumplimiento",   value: counts.ok || 0,    color: "emerald" },
    { key: "amber", label: "En riesgo",         value: counts.amber || 0, color: "amber" },
    { key: "red",   label: "No conforme",       value: counts.red || 0,   color: "red" },
    { key: "sin_evaluar", label: "Sin evaluar", value: counts.sin_evaluar || 0, color: "slate" },
    { key: "na",    label: "No aplica",         value: counts.na || 0,    color: "slate" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="pg07-kpis">
      {kpis.map(k => (
        <div key={k.key} className={`rounded-xl border p-4 bg-${k.color}-50/60 dark:bg-${k.color}-950/20`} data-testid={`pg07-kpi-${k.key}`}>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k.label}</div>
          <div className={`text-3xl font-black text-${k.color}-700 dark:text-${k.color}-300 mt-1`}>{k.value}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{pct(k.value)}% del total</div>
        </div>
      ))}
    </div>
  );
}

// ─── Compliance Matrix ───────────────────────────────────────────────────
function ComplianceMatrix({ rows, onSelect }) {
  if (!rows?.length) {
    return <Card><CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="pg07-empty">
      Sin proyectos importados. Contacta con un administrador para cargarlos.
    </CardContent></Card>;
  }
  return (
    <Card>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="pg07-matrix">
          <thead>
            <tr className="border-b bg-muted/30">
              <th className="text-left px-3 py-2 font-medium">Proyecto</th>
              <th className="text-left px-3 py-2 font-medium">PM</th>
              <th className="text-left px-3 py-2 font-medium">Fase</th>
              <th className="text-center px-3 py-2 font-medium" title="PG-07 F.01 Riesgos">F.01</th>
              <th className="text-center px-3 py-2 font-medium" title="PG-07 F.02 Colaboradores">F.02</th>
              <th className="text-center px-3 py-2 font-medium" title="Seguimiento trimestral">Q Rev.</th>
              <th className="text-center px-3 py-2 font-medium" title="Aprobación (firma dual)">Aprob.</th>
              <th className="text-center px-3 py-2 font-medium">Desviación</th>
              <th className="text-center px-3 py-2 font-medium">Estado</th>
              <th className="w-6"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.project_id}
                  className="border-b hover:bg-muted/40 cursor-pointer transition-colors"
                  onClick={() => onSelect(r)}
                  data-testid={`pg07-row-${i}`}>
                <td className="px-3 py-2">
                  <div className="font-medium truncate max-w-[280px]" title={r.name}>{r.name}</div>
                  {r.is_pg07_managed && (
                    <Badge variant="outline" className="mt-0.5 text-[9px] border-emerald-300 text-emerald-700">PG-07 formal</Badge>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[160px]" title={r.pm_email || ""}>
                  {r.pm_name || "—"}
                </td>
                <td className="px-3 py-2"><Badge variant="secondary" className="text-[10px]">{r.phase}</Badge></td>
                <td className="px-3 py-2 text-center"><StatusDot status={r.checks.f01_risks} /></td>
                <td className="px-3 py-2 text-center"><StatusDot status={r.checks.f02_collaborators} /></td>
                <td className="px-3 py-2 text-center"><StatusDot status={r.checks.quarterly_review} /></td>
                <td className="px-3 py-2 text-center"><StatusDot status={r.checks.approval} /></td>
                <td className="px-3 py-2 text-center">
                  {r.deviations?.length > 0 ? (
                    <span className="inline-flex items-center gap-1 text-red-700 text-[11px] font-semibold" data-testid={`pg07-deviation-${i}`}>
                      <AlertTriangle className="w-3 h-3" /> {r.deviations.length}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-[11px]">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center"><StatusPill status={r.global_status} /></td>
                <td className="px-3 py-2 text-muted-foreground"><ChevronRight className="w-4 h-4" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

// ─── PM Ranking ──────────────────────────────────────────────────────────
function PMRankingView({ authH, onSelectProject }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [filterPhase, setFilterPhase] = useState("");
  const [filterLine, setFilterLine] = useState("");
  const [savingPM, setSavingPM] = useState(null); // project_id being saved
  const [bulkSelected, setBulkSelected] = useState({}); // project_id → true (only from sin PM bucket)
  const [bulkPMValue, setBulkPMValue] = useState(""); // "email|name"
  const [bulkCustomEmail, setBulkCustomEmail] = useState("");
  const [bulkCustomName, setBulkCustomName] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/pg07/compliance/by-pm`, { headers: authH });
      const j = await r.json();
      setData(j);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [authH]);

  useEffect(() => { load(); }, [load]);

  const assignPM = async (project_id, pm_email, pm_name) => {
    setSavingPM(project_id);
    try {
      const r = await fetch(`${API}/pg07/projects/${project_id}`, {
        method: "PATCH",
        headers: { ...authH, "Content-Type": "application/json" },
        body: JSON.stringify({ pm_email, pm_name }),
      });
      if (r.ok) { toast.success(`PM asignado: ${pm_name || pm_email}`); load(); }
      else toast.error(`Error asignando PM (${r.status})`);
    } finally { setSavingPM(null); }
  };

  const bulkAssign = async () => {
    const project_ids = Object.keys(bulkSelected).filter(id => bulkSelected[id]);
    if (!project_ids.length) { toast.error("Selecciona proyectos primero"); return; }
    let pm_email = null, pm_name = null;
    if (bulkPMValue === "__custom__") {
      pm_email = bulkCustomEmail.trim() || null;
      pm_name = bulkCustomName.trim() || null;
      if (!pm_email && !pm_name) { toast.error("Introduce email o nombre"); return; }
    } else if (bulkPMValue) {
      const [em, nm] = bulkPMValue.split("|");
      pm_email = em || null;
      pm_name = nm || null;
    } else {
      toast.error("Selecciona un PM"); return;
    }
    setBulkSaving(true);
    try {
      const r = await fetch(`${API}/pg07/projects/bulk-assign-pm`, {
        method: "POST",
        headers: { ...authH, "Content-Type": "application/json" },
        body: JSON.stringify({ project_ids, pm_email, pm_name }),
      });
      if (r.ok) {
        const j = await r.json();
        toast.success(`${j.modified} proyectos asignados a ${pm_name || pm_email}`);
        setBulkSelected({}); setBulkPMValue(""); setBulkCustomEmail(""); setBulkCustomName("");
        load();
      } else toast.error(`Error (${r.status})`);
    } finally { setBulkSaving(false); }
  };

  if (loading) return <div className="p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;
  if (!data || !data.pms?.length) return <Card><CardContent className="p-8 text-center text-sm text-muted-foreground" data-testid="pg07-ranking-empty">Sin datos de ranking.</CardContent></Card>;

  const statusColor = (s) => s === "ok" ? "bg-emerald-500" : s === "amber" ? "bg-amber-500" : s === "red" ? "bg-red-500" : s === "sin_evaluar" ? "bg-slate-400" : "bg-slate-300";

  // Extract unique phases and lines from projects across all PMs for filter options
  const allPhases = new Set();
  const allLines = new Set();
  data.pms.forEach(pm => (pm.projects || []).forEach(pr => {
    if (pr.phase) allPhases.add(pr.phase);
    if (pr.business_line) allLines.add(pr.business_line);
  }));
  const phaseOpts = Array.from(allPhases).sort();
  const lineOpts = Array.from(allLines).sort();

  // Named PMs candidates (for reassignment dropdown) — exclude the "(sin PM)" bucket (no email AND no name)
  const pmCandidates = data.pms
    .filter(pm => (pm.pm_email || pm.pm_name))
    .map(pm => ({ email: pm.pm_email || null, name: pm.pm_name || (pm.pm_email ? pm.pm_email.split("@")[0] : "") }));

  // Filter PMs: keep PM if it has at least one project matching filters
  const matchesFilter = (proj) => {
    if (filterPhase && proj.phase !== filterPhase) return false;
    if (filterLine && proj.business_line !== filterLine) return false;
    return true;
  };
  const filteredPMs = (filterPhase || filterLine)
    ? data.pms
        .map(pm => ({ ...pm, projects: (pm.projects || []).filter(matchesFilter) }))
        .filter(pm => (pm.projects || []).length > 0)
    : data.pms;

  const selectedCount = Object.values(bulkSelected).filter(Boolean).length;

  return (
    <div className="space-y-3" data-testid="pg07-ranking-view">
      <div className="flex items-center gap-2 flex-wrap p-3 bg-slate-50 dark:bg-slate-800/40 rounded border" data-testid="pg07-ranking-filters">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Filtrar:</span>
        <select value={filterPhase} onChange={e => setFilterPhase(e.target.value)} className="h-7 text-xs rounded border px-2 bg-transparent" data-testid="pg07-ranking-filter-phase">
          <option value="">Todas las fases</option>
          {phaseOpts.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filterLine} onChange={e => setFilterLine(e.target.value)} className="h-7 text-xs rounded border px-2 bg-transparent min-w-[200px]" data-testid="pg07-ranking-filter-line">
          <option value="">Todas las líneas</option>
          {lineOpts.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        {(filterPhase || filterLine) && (
          <button className="text-xs text-emerald-700 hover:underline" onClick={() => { setFilterPhase(""); setFilterLine(""); }} data-testid="pg07-ranking-filter-clear">Limpiar</button>
        )}
        <span className="ml-auto text-xs text-muted-foreground">{filteredPMs.length}/{data.pms.length} PMs · {filteredPMs.reduce((a, p) => a + (p.projects?.length || 0), 0)} proyectos</span>
      </div>

      <div className="text-xs text-muted-foreground">
        Métrica: <strong>% fichas al día</strong> = fichas OK / (fichas OK + amber + rojo). Ordenados de peor a mejor cumplimiento. <strong>Haz click en un PM para ver y editar sus proyectos.</strong>
      </div>

      {selectedCount > 0 && (
        <div className="sticky top-2 z-20 flex items-center gap-2 flex-wrap p-3 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-300 rounded shadow-md" data-testid="pg07-bulk-toolbar">
          <Badge className="bg-emerald-600 text-white">{selectedCount} proyecto(s) sin PM seleccionado(s)</Badge>
          <span className="text-xs">Asignar a:</span>
          <select value={bulkPMValue} onChange={e => setBulkPMValue(e.target.value)} className="h-7 text-xs rounded border px-1.5 bg-transparent min-w-[200px]" data-testid="pg07-bulk-pm-select">
            <option value="">— Seleccionar PM —</option>
            {pmCandidates.map(c => <option key={`${c.email || ""}|${c.name}`} value={`${c.email || ""}|${c.name}`}>{c.name}{c.email ? ` (${c.email})` : ""}</option>)}
            <option value="__custom__">➕ Nuevo PM (personalizado)</option>
          </select>
          {bulkPMValue === "__custom__" && (
            <>
              <input placeholder="Nombre" value={bulkCustomName} onChange={e => setBulkCustomName(e.target.value)} className="h-7 text-xs rounded border px-2 bg-transparent max-w-[140px]" data-testid="pg07-bulk-custom-name" />
              <input placeholder="email@valorizasm.com" value={bulkCustomEmail} onChange={e => setBulkCustomEmail(e.target.value)} className="h-7 text-xs rounded border px-2 bg-transparent max-w-[200px]" data-testid="pg07-bulk-custom-email" />
            </>
          )}
          <Button size="sm" onClick={bulkAssign} disabled={bulkSaving || !bulkPMValue} data-testid="pg07-bulk-apply">
            {bulkSaving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
            Aplicar
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setBulkSelected({})} data-testid="pg07-bulk-clear">Limpiar selección</Button>
        </div>
      )}
      <div className="space-y-2">
        {filteredPMs.map((p, i) => {
          const pct = p.pct_al_dia ?? 0;
          const bar = pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-red-500";
          const trophy = i === filteredPMs.length - 1 && pct >= 80;
          const key = p.pm_email || p.pm_name || String(i);
          const isOpen = !!expanded[key];
          const isSinPM = !p.pm_email && !p.pm_name;
          const missingEmail = !p.pm_email && p.pm_name;
          return (
            <Card key={key} data-testid={`pg07-rank-${i}`} className={isSinPM ? "border-amber-300" : ""}>
              <CardContent className="p-4">
                <button
                  className="w-full flex items-center gap-3 flex-wrap text-left"
                  onClick={() => setExpanded(prev => ({ ...prev, [key]: !prev[key] }))}
                  data-testid={`pg07-rank-toggle-${i}`}
                >
                  <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${isSinPM ? "bg-amber-100 text-amber-800" : "bg-muted"}`}>
                    {isSinPM ? "?" : i + 1}
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-semibold">
                      {p.pm_name || p.pm_email || "(sin PM)"}
                      {trophy && <Trophy className="w-4 h-4 inline text-amber-500 ml-1" />}
                      {isSinPM && <Badge className="ml-2 bg-amber-100 text-amber-800 text-[10px]">Requiere asignación</Badge>}
                      {missingEmail && <Badge className="ml-2 bg-slate-100 text-slate-700 text-[10px]">Sin email</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground">{p.pm_email || (missingEmail ? "(email no registrado)" : "—")} · {p.total} proyecto(s){(filterPhase || filterLine) && ` · ${p.projects?.length || 0} filtrado(s)`}</div>
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="text-emerald-700 font-semibold">{p.ok} OK</span>
                    <span className="text-amber-700 font-semibold">{p.amber} amber</span>
                    <span className="text-red-700 font-semibold">{p.red} red</span>
                    <span className="text-slate-500 font-semibold">{p.sin_evaluar || 0} sin evaluar</span>
                    <span className="text-muted-foreground">{p.na} no aplica</span>
                  </div>
                  <div className="w-40 text-right">
                    <div className={`text-2xl font-black ${pct >= 80 ? "text-emerald-700" : pct >= 50 ? "text-amber-700" : "text-red-700"}`}>
                      {pct !== null ? `${pct}%` : "—"}
                    </div>
                    <div className="mt-1 h-2 bg-slate-100 rounded overflow-hidden">
                      <div className={`h-full ${bar} transition-all`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </button>
                {isOpen && (
                  <div className="mt-3 pt-3 border-t space-y-1" data-testid={`pg07-rank-projects-${i}`}>
                    {(p.projects || []).length === 0 && <div className="text-xs text-muted-foreground italic">Sin proyectos asignados.</div>}
                    {isSinPM && (p.projects || []).length > 0 && (
                      <div className="flex items-center gap-2 text-xs mb-1 pb-1 border-b">
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={(p.projects || []).every(pr => bulkSelected[pr.id])}
                            onChange={e => {
                              const next = { ...bulkSelected };
                              (p.projects || []).forEach(pr => { next[pr.id] = e.target.checked; });
                              setBulkSelected(next);
                            }}
                            data-testid={`pg07-rank-bulk-all-${i}`}
                          />
                          Seleccionar todos ({(p.projects || []).length})
                        </label>
                      </div>
                    )}
                    {(p.projects || []).map((proj, k) => (
                      <div key={proj.id} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-slate-50 dark:hover:bg-slate-800/40 flex-wrap" data-testid={`pg07-rank-project-${i}-${k}`}>
                        {isSinPM && (
                          <input
                            type="checkbox"
                            checked={!!bulkSelected[proj.id]}
                            onChange={e => setBulkSelected(prev => ({ ...prev, [proj.id]: e.target.checked }))}
                            className="flex-shrink-0"
                            data-testid={`pg07-rank-bulk-check-${i}-${k}`}
                          />
                        )}
                        <span className={`w-2 h-2 rounded-full ${statusColor(proj.status)} flex-shrink-0`} title={proj.status} />
                        <div className="flex-1 min-w-[220px]">
                          <div className="text-sm font-medium truncate">{proj.name}</div>
                          <div className="text-[10px] text-muted-foreground">{proj.phase || "—"} · {proj.business_line || "—"} · {proj.status?.toUpperCase() || "—"}</div>
                        </div>
                        {isSinPM && pmCandidates.length > 0 && (
                          <select
                            defaultValue=""
                            disabled={savingPM === proj.id}
                            onChange={e => {
                              const [email, name] = e.target.value.split("|");
                              if (email || name) assignPM(proj.id, email || null, name);
                            }}
                            className="h-7 text-xs rounded border px-1.5 bg-transparent max-w-[200px]"
                            data-testid={`pg07-rank-assign-pm-${i}-${k}`}
                          >
                            <option value="">— Asignar PM —</option>
                            {pmCandidates.map(c => <option key={`${c.email || ""}|${c.name}`} value={`${c.email || ""}|${c.name}`}>{c.name}{c.email ? ` (${c.email})` : ""}</option>)}
                          </select>
                        )}
                        {savingPM === proj.id && <Loader2 className="w-3 h-3 animate-spin text-emerald-600" />}
                        <Button size="sm" variant="outline" onClick={() => onSelectProject?.(proj.id)} data-testid={`pg07-rank-edit-fichas-${i}-${k}`}>
                          <FileWarning className="w-3 h-3 mr-1" />Fichas PG-07
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/pg07/extras?project=${proj.id}`)} data-testid={`pg07-rank-open-iso-${i}-${k}`}>
                          <ExternalLink className="w-3 h-3 mr-1" />ISO & Cierre
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ─── Ficha Editors ────────────────────────────────────────────────────────
function RiskForm({ projectId, risk, authH, onSaved, onCancel }) {
  const [d, setD] = useState(risk || {
    description: "", corrective_actions: "", impact: 3, probability: 3,
    quantitative_impact_eur: null, quantitative_delay_days: null,
    q1_follow_up: "", q2_follow_up: "", q3_follow_up: "", q4_follow_up: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!d.description?.trim()) { toast.error("La descripción es obligatoria"); return; }
    setSaving(true);
    try {
      const url = risk?.id
        ? `${API}/pg07/projects/${projectId}/risks/${risk.id}`
        : `${API}/pg07/projects/${projectId}/risks`;
      const r = await fetch(url, {
        method: risk?.id ? "PUT" : "POST",
        headers: { ...authH, "Content-Type": "application/json" },
        body: JSON.stringify(d),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Error");
      toast.success("Riesgo guardado");
      onSaved();
    } catch (e) { toast.error(String(e?.message || "Error")); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3 text-sm" data-testid="pg07-risk-form">
      <Field label="Descripción del riesgo *">
        <Textarea rows={2} value={d.description || ""} onChange={e => setD({...d, description: e.target.value})} data-testid="pg07-risk-description" />
      </Field>
      <Field label="Acciones correctivas">
        <Textarea rows={2} value={d.corrective_actions || ""} onChange={e => setD({...d, corrective_actions: e.target.value})} data-testid="pg07-risk-actions" />
      </Field>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Field label="Impacto (1-5)"><Input type="number" min={1} max={5} value={d.impact ?? ""} onChange={e => setD({...d, impact: parseInt(e.target.value) || null})} data-testid="pg07-risk-impact" /></Field>
        <Field label="Probabilidad (1-5)"><Input type="number" min={1} max={5} value={d.probability ?? ""} onChange={e => setD({...d, probability: parseInt(e.target.value) || null})} data-testid="pg07-risk-probability" /></Field>
        <Field label="Impacto € (cuant.)"><Input type="number" step="0.01" value={d.quantitative_impact_eur ?? ""} onChange={e => setD({...d, quantitative_impact_eur: parseFloat(e.target.value) || null})} data-testid="pg07-risk-impact-eur" /></Field>
        <Field label="Retraso días (cuant.)"><Input type="number" value={d.quantitative_delay_days ?? ""} onChange={e => setD({...d, quantitative_delay_days: parseInt(e.target.value) || null})} data-testid="pg07-risk-delay-days" /></Field>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {QUARTERS.map((q, i) => (
          <Field key={q} label={`Seguimiento ${q}`}>
            <Textarea rows={2} value={d[`q${i+1}_follow_up`] || ""} onChange={e => setD({...d, [`q${i+1}_follow_up`]: e.target.value})} data-testid={`pg07-risk-${q.toLowerCase()}`} />
          </Field>
        ))}
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={saving} data-testid="pg07-risk-cancel">Cancelar</Button>
        <Button onClick={save} disabled={saving} data-testid="pg07-risk-save">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Guardar riesgo</Button>
      </div>
    </div>
  );
}

function CollaboratorForm({ projectId, collab, authH, onSaved, onCancel }) {
  const [d, setD] = useState(collab || { name: "", score_plazos: null, score_comunicacion: null, score_confidencialidad: null, score_calidad: null, notes: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!d.name?.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      const url = collab?.id ? `${API}/pg07/projects/${projectId}/collaborators/${collab.id}` : `${API}/pg07/projects/${projectId}/collaborators`;
      const r = await fetch(url, { method: collab?.id ? "PUT" : "POST", headers: { ...authH, "Content-Type": "application/json" }, body: JSON.stringify(d) });
      if (!r.ok) throw new Error((await r.json()).detail || "Error");
      toast.success("Colaborador guardado");
      onSaved();
    } catch (e) { toast.error(String(e?.message || "Error")); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-3 text-sm" data-testid="pg07-collab-form">
      <Field label="Nombre del colaborador *">
        <Input value={d.name || ""} onChange={e => setD({...d, name: e.target.value})} data-testid="pg07-collab-name" />
      </Field>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {[
          ["score_plazos", "Cumplimiento plazos"],
          ["score_comunicacion", "Comunicación"],
          ["score_confidencialidad", "Confidencialidad"],
          ["score_calidad", "Calidad servicio"],
        ].map(([k, label]) => (
          <Field key={k} label={`${label} (0-10)`}>
            <Input type="number" min={0} max={10} value={d[k] ?? ""} onChange={e => setD({...d, [k]: e.target.value === "" ? null : parseInt(e.target.value)})} data-testid={`pg07-collab-${k.replace("score_", "")}`} />
          </Field>
        ))}
      </div>
      <Field label="Notas"><Textarea rows={2} value={d.notes || ""} onChange={e => setD({...d, notes: e.target.value})} data-testid="pg07-collab-notes" /></Field>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={saving}>Cancelar</Button>
        <Button onClick={save} disabled={saving} data-testid="pg07-collab-save">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Guardar colaborador</Button>
      </div>
    </div>
  );
}

function QReviewForm({ projectId, review, authH, onSaved, onCancel }) {
  const [d, setD] = useState(review || { quarter: "Q3", avances_tecnicos: "", cambios_tecnicos: "", hechos_relevantes: "", desviacion_plazo_pct: null, desviacion_coste_pct: null, proteccion_resultados: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const url = review?.id ? `${API}/pg07/projects/${projectId}/quarterly-reviews/${review.id}` : `${API}/pg07/projects/${projectId}/quarterly-reviews`;
      const r = await fetch(url, { method: review?.id ? "PUT" : "POST", headers: { ...authH, "Content-Type": "application/json" }, body: JSON.stringify(d) });
      if (!r.ok) throw new Error((await r.json()).detail || "Error");
      const dp = Math.abs(d.desviacion_plazo_pct || 0);
      const dc = Math.abs(d.desviacion_coste_pct || 0);
      if (dp > 10 || dc > 10) {
        toast.warning(`⚠️ Desviación >10% detectada — se registrará como No Conformidad. Se ha notificado al equipo.`);
      } else {
        toast.success("Seguimiento guardado");
      }
      onSaved();
    } catch (e) { toast.error(String(e?.message || "Error")); }
    finally { setSaving(false); }
  };
  const deviatedP = d.desviacion_plazo_pct !== null && Math.abs(d.desviacion_plazo_pct) > 10;
  const deviatedC = d.desviacion_coste_pct !== null && Math.abs(d.desviacion_coste_pct) > 10;
  return (
    <div className="space-y-3 text-sm" data-testid="pg07-qreview-form">
      <Field label="Trimestre">
        <Select value={d.quarter} onValueChange={v => setD({...d, quarter: v})}>
          <SelectTrigger data-testid="pg07-qreview-quarter"><SelectValue /></SelectTrigger>
          <SelectContent>{QUARTERS.map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Avances técnicos"><Textarea rows={2} value={d.avances_tecnicos || ""} onChange={e => setD({...d, avances_tecnicos: e.target.value})} data-testid="pg07-qreview-avances" /></Field>
      <Field label="Cambios técnicos"><Textarea rows={2} value={d.cambios_tecnicos || ""} onChange={e => setD({...d, cambios_tecnicos: e.target.value})} /></Field>
      <Field label="Hechos relevantes"><Textarea rows={2} value={d.hechos_relevantes || ""} onChange={e => setD({...d, hechos_relevantes: e.target.value})} /></Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label={<span>Desviación plazo (%) {deviatedP && <Badge variant="destructive" className="ml-1 text-[9px]">&gt;10%</Badge>}</span>}>
          <Input type="number" step="0.1" value={d.desviacion_plazo_pct ?? ""} onChange={e => setD({...d, desviacion_plazo_pct: e.target.value === "" ? null : parseFloat(e.target.value)})} data-testid="pg07-qreview-dev-plazo" />
        </Field>
        <Field label={<span>Desviación coste (%) {deviatedC && <Badge variant="destructive" className="ml-1 text-[9px]">&gt;10%</Badge>}</span>}>
          <Input type="number" step="0.1" value={d.desviacion_coste_pct ?? ""} onChange={e => setD({...d, desviacion_coste_pct: e.target.value === "" ? null : parseFloat(e.target.value)})} data-testid="pg07-qreview-dev-coste" />
        </Field>
      </div>
      <Field label="Protección de resultados"><Textarea rows={2} value={d.proteccion_resultados || ""} onChange={e => setD({...d, proteccion_resultados: e.target.value})} /></Field>
      {(deviatedP || deviatedC) && (
        <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/30 p-3 text-xs text-red-700 dark:text-red-300 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Al guardar se abrirá una <strong>No Conformidad</strong> automática y se notificará al PM por email + Teams.</span>
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={saving}>Cancelar</Button>
        <Button onClick={save} disabled={saving} data-testid="pg07-qreview-save">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Guardar seguimiento</Button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

// ─── Project Detail (with editable fichas tabs) ───────────────────────────
function ProjectDetailDialog({ open, onOpenChange, projectId, authH, onDataChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("data");
  const [editingRisk, setEditingRisk] = useState(null);
  const [editingCollab, setEditingCollab] = useState(null);
  const [editingReview, setEditingReview] = useState(null);
  const [editingPM, setEditingPM] = useState(false);
  const [pmForm, setPmForm] = useState({ pm_name: "", pm_email: "" });
  const [savingPM, setSavingPM] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/pg07/projects/${projectId}`, { headers: authH });
      setData(await r.json());
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [projectId, authH]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- load cambia de identidad tras firmar
  // (approve() → load()+onDataChanged()) y si estuviera en las deps, resetearía la pestaña a 'data'.
  useEffect(() => { if (open && projectId) { setTab("data"); setEditingRisk(null); setEditingCollab(null); setEditingReview(null); setEditingPM(false); load(); } }, [open, projectId]);

  const [deleteItem, setDeleteItem] = useState(null);

  const del = (kind, id, label) => setDeleteItem({ kind, id, label });

  const confirmDelItem = async () => {
    if (!deleteItem) return;
    try {
      const r = await fetch(`${API}/pg07/projects/${projectId}/${deleteItem.kind}/${deleteItem.id}`, { method: "DELETE", headers: authH });
      if (!r.ok) throw new Error("Error");
      toast.success("Eliminado");
      await load();
      onDataChanged?.();
    } catch { toast.error("Error eliminando"); }
    finally { setDeleteItem(null); }
  };

  const afterSave = async () => {
    setEditingRisk(null); setEditingCollab(null); setEditingReview(null);
    await load();
    onDataChanged?.();
  };

  const approve = async (role) => {
    try {
      const r = await fetch(`${API}/pg07/projects/${projectId}/approve`, {
        method: "POST", headers: { ...authH, "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Error");
      toast.success(`Firmado como ${role}`);
      await load();
      onDataChanged?.();
    } catch (e) { toast.error(String(e?.message || "Error firmando")); }
  };

  const savePM = async () => {
    setSavingPM(true);
    try {
      const r = await fetch(`${API}/pg07/projects/${projectId}`, {
        method: "PATCH",
        headers: { ...authH, "Content-Type": "application/json" },
        body: JSON.stringify({ pm_email: pmForm.pm_email.trim() || null, pm_name: pmForm.pm_name.trim() || null }),
      });
      if (!r.ok) throw new Error();
      toast.success("Responsable asignado");
      setEditingPM(false);
      await load();
      onDataChanged?.();
    } catch { toast.error("Error asignando responsable"); }
    finally { setSavingPM(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="pg07-detail-dialog">
        {loading || !data ? (
          <>
            <DialogTitle className="sr-only">Cargando proyecto PG-07</DialogTitle>
            <div className="p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-start justify-between gap-3">
                <span className="text-lg leading-tight" data-testid="pg07-detail-name">{data.name}</span>
                <Badge variant="secondary" className="mt-1 shrink-0">{data.phase}</Badge>
              </DialogTitle>
              <DialogDescription className="text-xs">
                {data.project_type && <>{data.project_type} · </>}
                Año {data.year}
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-lg border bg-muted/30 p-3" data-testid="pg07-detail-pm-block">
              {!editingPM ? (
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-sm">
                    <span className="text-muted-foreground">Responsable (PM): </span>
                    {data.pm_name || data.pm_email ? (
                      <strong>{data.pm_name || data.pm_email}</strong>
                    ) : (
                      <span className="text-amber-600 font-medium">Sin asignar</span>
                    )}
                  </div>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => { setPmForm({ pm_name: data.pm_name || "", pm_email: data.pm_email || "" }); setEditingPM(true); }}
                    data-testid="pg07-detail-pm-edit-btn"
                  >
                    {data.pm_name || data.pm_email ? "Reasignar" : "Asignar responsable"}
                  </Button>
                </div>
              ) : (
                <div className="flex items-end gap-2 flex-wrap">
                  <div className="flex-1 min-w-[160px]">
                    <Label className="text-xs">Nombre</Label>
                    <Input value={pmForm.pm_name} onChange={(e) => setPmForm(f => ({ ...f, pm_name: e.target.value }))} data-testid="pg07-detail-pm-name-input" />
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <Label className="text-xs">Email</Label>
                    <Input value={pmForm.pm_email} onChange={(e) => setPmForm(f => ({ ...f, pm_email: e.target.value }))} data-testid="pg07-detail-pm-email-input" />
                  </div>
                  <Button size="sm" onClick={savePM} disabled={savingPM} data-testid="pg07-detail-pm-save-btn">
                    {savingPM ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}Guardar
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingPM(false)} data-testid="pg07-detail-pm-cancel-btn">Cancelar</Button>
                </div>
              )}
            </div>

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="grid grid-cols-8">
                <TabsTrigger value="data" data-testid="pg07-tab-data">Datos</TabsTrigger>
                <TabsTrigger value="risks" data-testid="pg07-tab-risks"><FileWarning className="w-3 h-3 mr-1" />F.01 ({data.risks?.length || 0})</TabsTrigger>
                <TabsTrigger value="collabs" data-testid="pg07-tab-collabs"><Users2 className="w-3 h-3 mr-1" />F.02 ({data.collaborators?.length || 0})</TabsTrigger>
                <TabsTrigger value="reviews" data-testid="pg07-tab-reviews"><Clock className="w-3 h-3 mr-1" />Q Rev. ({data.quarterly_reviews?.length || 0})</TabsTrigger>
                <TabsTrigger value="approval" data-testid="pg07-tab-approval"><Signature className="w-3 h-3 mr-1" />Aprob.</TabsTrigger>
                <TabsTrigger value="meetings" data-testid="pg07-tab-meetings"><FileWarning className="w-3 h-3 mr-1" />Reuniones</TabsTrigger>
                <TabsTrigger value="vt" data-testid="pg07-tab-vt"><Radar className="w-3 h-3 mr-1" />Vigilancia</TabsTrigger>
                <TabsTrigger value="invoices" data-testid="pg07-tab-invoices"><Receipt className="w-3 h-3 mr-1" />Facturas</TabsTrigger>
              </TabsList>

              <TabsContent value="data" className="mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <Section title="Objetivo">{data.objective || "—"}</Section>
                  <Section title="Beneficio esperado">{data.expected_benefit || "—"}</Section>
                  <Section title="Situación de partida">{data.starting_situation || "—"}</Section>
                  <Section title="Lógica pilotos">{data.pilot_logic || "—"}</Section>
                  <Section title="Presupuesto previsto">{data.budget_planned ? new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(data.budget_planned) : "—"}</Section>
                  <Section title="Convocatoria">{data.call_program || "—"}</Section>
                </div>
                {data.tracking_by_year && Object.keys(data.tracking_by_year).length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Seguimiento histórico</h4>
                    <div className="space-y-2">
                      {Object.entries(data.tracking_by_year).sort((a,b) => b[0].localeCompare(a[0])).map(([y, txt]) => (
                        <div key={y} className="border-l-2 border-emerald-500 pl-3">
                          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">{y}</div>
                          <div className="text-xs whitespace-pre-wrap max-h-32 overflow-y-auto">{txt}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="risks" className="mt-4 space-y-3">
                {editingRisk !== null ? (
                  <RiskForm projectId={projectId} risk={editingRisk === "new" ? null : editingRisk} authH={authH} onSaved={afterSave} onCancel={() => setEditingRisk(null)} />
                ) : (
                  <>
                    <div className="flex justify-between items-center">
                      <div className="text-xs text-muted-foreground">Ficha F.01 · Identificación y gestión de riesgos (trimestral)</div>
                      <Button size="sm" onClick={() => setEditingRisk("new")} data-testid="pg07-risk-add"><Plus className="w-3 h-3 mr-1" />Añadir riesgo</Button>
                    </div>
                    {(data.risks || []).length === 0 ? (
                      <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">Sin riesgos registrados.</CardContent></Card>
                    ) : (
                      data.risks.map(r => (
                        <Card key={r.id} data-testid={`pg07-risk-item-${r.id}`}>
                          <CardContent className="p-3 text-sm">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <div className="font-semibold">{r.description}</div>
                                <div className="text-xs text-muted-foreground mt-0.5">
                                  {r.impact && r.probability && <span>Impacto {r.impact}/5 · Prob {r.probability}/5 · </span>}
                                  {r.quantitative_impact_eur && <span>{new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(r.quantitative_impact_eur)} · </span>}
                                  {r.quantitative_delay_days && <span>{r.quantitative_delay_days} días · </span>}
                                  {r.year}
                                </div>
                                {r.corrective_actions && <div className="text-xs mt-1"><strong>Acciones:</strong> {r.corrective_actions}</div>}
                                <div className="grid grid-cols-4 gap-2 mt-2 text-[11px]">
                                  {[1,2,3,4].map(i => {
                                    const val = r[`q${i}_follow_up`];
                                    return (
                                      <div key={i} className={`p-1.5 rounded border ${val ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
                                        <div className="text-[9px] uppercase font-semibold text-muted-foreground">Q{i}</div>
                                        <div className="truncate">{val || "—"}</div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                              <div className="flex flex-col gap-1">
                                <Button size="sm" variant="ghost" onClick={() => setEditingRisk(r)} data-testid={`pg07-risk-edit-${r.id}`}>Editar</Button>
                                <Button size="sm" variant="ghost" onClick={() => del("risks", r.id, r.description)} data-testid={`pg07-risk-delete-${r.id}`}><Trash2 className="w-3 h-3 text-red-600" /></Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </>
                )}
              </TabsContent>

              <TabsContent value="collabs" className="mt-4 space-y-3">
                {editingCollab !== null ? (
                  <CollaboratorForm projectId={projectId} collab={editingCollab === "new" ? null : editingCollab} authH={authH} onSaved={afterSave} onCancel={() => setEditingCollab(null)} />
                ) : (
                  <>
                    <div className="flex justify-between items-center">
                      <div className="text-xs text-muted-foreground">Ficha F.02 · Identificación y evaluación de colaboradores (anual · dic)</div>
                      <Button size="sm" onClick={() => setEditingCollab("new")} data-testid="pg07-collab-add"><Plus className="w-3 h-3 mr-1" />Añadir colaborador</Button>
                    </div>
                    {(data.collaborators || []).length === 0 ? (
                      <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">Sin colaboradores evaluados.</CardContent></Card>
                    ) : (
                      data.collaborators.map(c => {
                        const avg = [c.score_plazos, c.score_comunicacion, c.score_confidencialidad, c.score_calidad].filter(v => v !== null && v !== undefined);
                        const mean = avg.length ? (avg.reduce((a,b) => a+b, 0) / avg.length).toFixed(1) : "—";
                        return (
                          <Card key={c.id} data-testid={`pg07-collab-item-${c.id}`}>
                            <CardContent className="p-3 text-sm flex items-center gap-3">
                              <div className="flex-1">
                                <div className="font-semibold">{c.name}</div>
                                <div className="text-xs text-muted-foreground">{c.year} · Media: <strong>{mean}/10</strong></div>
                                <div className="text-[10px] mt-1 text-muted-foreground">
                                  Plazos: {c.score_plazos ?? "—"} · Com: {c.score_comunicacion ?? "—"} · Conf: {c.score_confidencialidad ?? "—"} · Cal: {c.score_calidad ?? "—"}
                                </div>
                                {c.notes && <div className="text-xs mt-1">{c.notes}</div>}
                              </div>
                              <Button size="sm" variant="ghost" onClick={() => setEditingCollab(c)}>Editar</Button>
                              <Button size="sm" variant="ghost" onClick={() => del("collaborators", c.id, c.name)} data-testid={`pg07-collab-delete-${c.id}`}><Trash2 className="w-3 h-3 text-red-600" /></Button>
                            </CardContent>
                          </Card>
                        );
                      })
                    )}
                  </>
                )}
              </TabsContent>

              <TabsContent value="reviews" className="mt-4 space-y-3">
                {editingReview !== null ? (
                  <QReviewForm projectId={projectId} review={editingReview === "new" ? null : editingReview} authH={authH} onSaved={afterSave} onCancel={() => setEditingReview(null)} />
                ) : (
                  <>
                    <div className="flex justify-between items-center">
                      <div className="text-xs text-muted-foreground">Seguimiento trimestral con detección auto &gt;10%</div>
                      <Button size="sm" onClick={() => setEditingReview("new")} data-testid="pg07-qreview-add"><Plus className="w-3 h-3 mr-1" />Añadir seguimiento</Button>
                    </div>
                    {(data.quarterly_reviews || []).length === 0 ? (
                      <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">Sin seguimientos registrados.</CardContent></Card>
                    ) : (
                      data.quarterly_reviews.map(r => {
                        const dp = Math.abs(r.desviacion_plazo_pct || 0) > 10;
                        const dc = Math.abs(r.desviacion_coste_pct || 0) > 10;
                        return (
                          <Card key={r.id} data-testid={`pg07-qreview-item-${r.id}`}>
                            <CardContent className="p-3 text-sm">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <Badge>{r.year} {r.quarter}</Badge>
                                    {dp && <Badge variant="destructive" className="text-[9px]">Plazo {r.desviacion_plazo_pct}%</Badge>}
                                    {dc && <Badge variant="destructive" className="text-[9px]">Coste {r.desviacion_coste_pct}%</Badge>}
                                  </div>
                                  {r.avances_tecnicos && <div className="mt-2 text-xs"><strong>Avances:</strong> {r.avances_tecnicos}</div>}
                                  {r.cambios_tecnicos && <div className="text-xs"><strong>Cambios:</strong> {r.cambios_tecnicos}</div>}
                                  {r.hechos_relevantes && <div className="text-xs"><strong>Hechos:</strong> {r.hechos_relevantes}</div>}
                                  {r.proteccion_resultados && <div className="text-xs"><strong>Protección:</strong> {r.proteccion_resultados}</div>}
                                </div>
                                <div className="flex flex-col gap-1">
                                  <Button size="sm" variant="ghost" onClick={() => setEditingReview(r)}>Editar</Button>
                                  <Button size="sm" variant="ghost" onClick={() => del("quarterly-reviews", r.id, `${r.quarter} ${r.year}`)} data-testid={`pg07-qreview-delete-${r.id}`}><Trash2 className="w-3 h-3 text-red-600" /></Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })
                    )}
                  </>
                )}
              </TabsContent>

              <TabsContent value="approval" className="mt-4">
                <div className="text-xs text-muted-foreground mb-3">
                  El PG-07 requiere firma dual al pasar a fase <strong>Piloto</strong> o <strong>Desarrollo</strong>.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <ApprovalCard title="Sistema de Gestión" name="Blanca Almendro" role="sistema"
                    signed={data.approval?.approved_by_sistema}
                    signedAt={data.approval?.approved_by_sistema_at}
                    signedBy={data.approval?.approved_by_sistema_email}
                    onSign={() => approve("sistema")} />
                  <ApprovalCard title="Director Innovación IA" name="David Redondo" role="director"
                    signed={data.approval?.approved_by_director}
                    signedAt={data.approval?.approved_by_director_at}
                    signedBy={data.approval?.approved_by_director_email}
                    onSign={() => approve("director")} />
                </div>
                {data.approval?.approved_by_sistema && data.approval?.approved_by_director && (
                  <div className="mt-3 p-3 rounded border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 text-xs flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" /> Proyecto aprobado con firma dual. Cumple PG-07 § 5.
                  </div>
                )}
              </TabsContent>

              <TabsContent value="meetings" className="mt-4">
                <MeetingsTab projectId={projectId} authH={authH} />
              </TabsContent>

              <TabsContent value="vt" className="mt-4">
                <VigilanciaTab projectId={projectId} projectName={data.name} authH={authH} />
              </TabsContent>

              <TabsContent value="invoices" className="mt-4">
                <PG07InvoicesPanel authH={authH} projectId={projectId} projectName={data.name} />
              </TabsContent>
            </Tabs>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pg07-detail-close">Cerrar</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>

      <ConfirmDialog
        open={!!deleteItem}
        onOpenChange={(v) => !v && setDeleteItem(null)}
        title="Eliminar registro"
        description={`¿Estás seguro de que quieres eliminar "${deleteItem?.label || "este registro"}"? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        onConfirm={confirmDelItem}
        testId="delete-pg07-item-confirm-dialog"
      />
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">{title}</div>
      <div className="text-xs whitespace-pre-wrap">{children}</div>
    </div>
  );
}

function MeetingsTab({ projectId, authH }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkDialog, setLinkDialog] = useState(false);
  const [unlinked, setUnlinked] = useState([]);
  const [linking, setLinking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/fireflies/transcripts?project_id=${projectId}`, { headers: authH });
      const j = await r.json();
      setItems(Array.isArray(j) ? j : []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [projectId, authH]);

  useEffect(() => { load(); }, [load]);

  const openLinkDialog = async () => {
    try {
      const r = await fetch(`${API}/fireflies/transcripts?unlinked_only=true&limit=200`, { headers: authH });
      const j = await r.json();
      setUnlinked(Array.isArray(j) ? j : []);
      setLinkDialog(true);
    } catch { toast.error("Error cargando transcripciones"); }
  };

  const linkTranscript = async (ffId) => {
    setLinking(true);
    try {
      const r = await fetch(`${API}/fireflies/transcripts/${ffId}/link`, {
        method: "POST",
        headers: { ...authH, "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: projectId }),
      });
      if (!r.ok) throw new Error("link failed");
      toast.success("Reunión vinculada");
      setLinkDialog(false);
      await load();
    } catch { toast.error("Error vinculando"); }
    finally { setLinking(false); }
  };

  const [unlinkTarget, setUnlinkTarget] = useState(null);

  const unlinkTranscript = (ffId) => setUnlinkTarget(ffId);

  const confirmUnlinkTranscript = async () => {
    if (!unlinkTarget) return;
    try {
      const r = await fetch(`${API}/fireflies/transcripts/${unlinkTarget}/link`, {
        method: "POST",
        headers: { ...authH, "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: null }),
      });
      if (!r.ok) throw new Error("unlink failed");
      toast.success("Reunión desvinculada");
      await load();
    } catch { toast.error("Error desvinculando"); }
    finally { setUnlinkTarget(null); }
  };

  if (loading) return <div className="p-6 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-600" /></div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">
          Transcripciones de Fireflies. Auto-vinculadas cuando el nombre del proyecto aparece en el título.
        </div>
        <Button size="sm" variant="outline" onClick={openLinkDialog} data-testid="pg07-meeting-link-btn">
          <Plus className="w-3 h-3 mr-1" />Vincular reunión existente
        </Button>
      </div>

      {(items || []).length === 0 ? (
        <Card><CardContent className="p-6 text-center text-sm text-muted-foreground" data-testid="pg07-meetings-empty">
          Sin reuniones vinculadas.
        </CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="pg07-meetings-list">
          {items.map(m => (
            <Card key={m.fireflies_id} data-testid={`pg07-meeting-${m.fireflies_id}`}>
              <CardContent className="p-3 text-sm">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="flex-1">
                    <div className="font-semibold">{m.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {m.date?.slice(0, 10)} · {m.duration_min ? `${Math.round(m.duration_min)} min` : "—"}
                      {m.organizer_email && <span> · {m.organizer_email}</span>}
                      {(m.participants || []).length > 0 && <span> · {m.participants.length} participante(s)</span>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {m.transcript_url && (
                      <a href={m.transcript_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 hover:underline">Ver en Fireflies →</a>
                    )}
                    <button
                      onClick={() => unlinkTranscript(m.fireflies_id)}
                      className="text-xs text-red-600 hover:underline"
                      data-testid={`pg07-meeting-unlink-${m.fireflies_id}`}
                    >Desvincular</button>
                  </div>
                </div>
                {m.summary_overview && (
                  <div className="mt-2 text-xs bg-muted/40 p-2 rounded whitespace-pre-wrap max-h-32 overflow-y-auto">
                    {m.summary_overview}
                  </div>
                )}
                {(m.summary_keywords || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {m.summary_keywords.slice(0, 8).map((k, i) => (
                      <Badge key={i} variant="outline" className="text-[9px]">{k}</Badge>
                    ))}
                  </div>
                )}
                {m.summary_actions && (
                  <div className="mt-2 text-xs">
                    <strong className="text-emerald-700">Acciones:</strong>
                    <div className="whitespace-pre-wrap max-h-24 overflow-y-auto mt-0.5">{m.summary_actions}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={linkDialog} onOpenChange={setLinkDialog}>
        <DialogContent className="max-w-2xl max-h-[70vh] overflow-y-auto" data-testid="pg07-meeting-link-dialog">
          <DialogHeader><DialogTitle>Vincular reunión al proyecto</DialogTitle></DialogHeader>
          <div className="text-xs text-muted-foreground mb-2">
            {unlinked.length} transcripciones sin vincular. Selecciona la que corresponda a este proyecto.
          </div>
          {unlinked.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">Todas las transcripciones ya están vinculadas.</div>
          ) : (
            <div className="space-y-2">
              {unlinked.map(u => (
                <button
                  key={u.fireflies_id}
                  onClick={() => linkTranscript(u.fireflies_id)}
                  disabled={linking}
                  className="w-full text-left p-3 border rounded hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-colors"
                  data-testid={`pg07-meeting-candidate-${u.fireflies_id}`}
                >
                  <div className="font-medium text-sm">{u.title}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {u.date?.slice(0, 10)} · {u.duration_min ? `${Math.round(u.duration_min)} min` : "—"}
                    {u.organizer_email && <span> · {u.organizer_email}</span>}
                  </div>
                  {u.summary_overview && (
                    <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{u.summary_overview}</div>
                  )}
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!unlinkTarget}
        onOpenChange={(v) => !v && setUnlinkTarget(null)}
        title="Desvincular reunión"
        description="¿Estás seguro de que quieres desvincular esta reunión del proyecto?"
        confirmLabel="Desvincular"
        onConfirm={confirmUnlinkTranscript}
        testId="unlink-meeting-confirm-dialog"
      />
    </div>
  );
}


// ─── Vigilancia Tecnológica tab (inline in project detail) ─────────────────
const VT_STAGE_META = {
  estado_arte: { label: "Estado del arte", color: "bg-sky-100 text-sky-800" },
  desarrollo:  { label: "Desarrollo",     color: "bg-violet-100 text-violet-800" },
  escalado:    { label: "Escalado",       color: "bg-amber-100 text-amber-800" },
  mercado:     { label: "Mercado",        color: "bg-emerald-100 text-emerald-800" },
};
const VT_SEV_COLOR = { info: "bg-slate-100 text-slate-700", media: "bg-amber-100 text-amber-800", alta: "bg-orange-100 text-orange-800", "crítica": "bg-red-100 text-red-800" };

function VigilanciaTab({ projectId, projectName, authH }) {
  const [hyps, setHyps] = useState([]);
  const [sigs, setSigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(null); // hypothesis_num or null
  const [newSig, setNewSig] = useState({ title: "", description: "", severity: "media" });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, s] = await Promise.all([
        fetch(`${API}/vt/projects/${projectId}/hypotheses`, { headers: authH }).then(r => r.json()),
        fetch(`${API}/vt/signals?project_id=${projectId}`, { headers: authH }).then(r => r.json()),
      ]);
      setHyps(h || []); setSigs(s || []);
    } finally { setLoading(false); }
  }, [projectId, authH]);

  useEffect(() => { load(); }, [load]);

  const patchHyp = async (hid, patch) => {
    const r = await fetch(`${API}/vt/hypotheses/${hid}`, {
      method: "PATCH", headers: { ...authH, "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (r.ok) load();
  };
  const createSig = async (hnum) => {
    if (!newSig.title.trim()) { toast.error("Título obligatorio"); return; }
    const r = await fetch(`${API}/vt/signals`, {
      method: "POST", headers: { ...authH, "Content-Type": "application/json" },
      body: JSON.stringify({ project_id: projectId, hypothesis_num: hnum, ...newSig }),
    });
    if (r.ok) {
      toast.success("Señal creada");
      setShowAdd(null); setNewSig({ title: "", description: "", severity: "media" });
      load();
    }
  };
  const closeSig = async (sid, status) => {
    const r = await fetch(`${API}/vt/signals/${sid}`, {
      method: "PATCH", headers: { ...authH, "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });
    if (r.ok) load();
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-cyan-600" /></div>;

  const openSignalCount = sigs.filter(s => s.status === "abierta" || s.status === "en_curso").length;
  const criticalCount = sigs.filter(s => (s.status === "abierta" || s.status === "en_curso") && (s.severity === "alta" || s.severity === "crítica")).length;

  return (
    <div className="space-y-3" data-testid={`pg07-vt-tab-${projectId}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-muted-foreground">
          <strong>{openSignalCount}</strong> señales abiertas · <strong className={criticalCount > 0 ? "text-red-700" : ""}>{criticalCount}</strong> críticas · <a href="https://www.antara.ws/es/blog/vigilancia-tecnologica-proyectos-id-10-preguntas-clave" target="_blank" rel="noopener noreferrer" className="text-cyan-700 hover:underline">10 preguntas Antara ↗</a>
        </div>
        <Button size="sm" variant="outline" onClick={() => navigate(`/vigilancia?project=${projectId}`)} data-testid="pg07-vt-open-full">
          <Radar className="w-3 h-3 mr-1" />Ver en Vigilancia
        </Button>
      </div>
      {["estado_arte", "desarrollo", "escalado", "mercado"].map(st => {
        const stageHyps = hyps.filter(h => h.stage === st);
        if (!stageHyps.length) return null;
        return (
          <div key={st}>
            <div className={`text-[10px] uppercase tracking-widest font-black mb-1 inline-block px-1.5 py-0.5 rounded ${VT_STAGE_META[st].color}`}>{VT_STAGE_META[st].label}</div>
            <div className="space-y-1.5">
              {stageHyps.map(h => {
                const hSigs = sigs.filter(s => s.hypothesis_num === h.hypothesis_num);
                return (
                  <div key={h.id} className="border rounded p-2" data-testid={`pg07-vt-hyp-${h.hypothesis_num}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="w-6 h-6 rounded-full bg-cyan-100 text-cyan-800 flex items-center justify-center font-bold text-[11px]">{h.hypothesis_num}</span>
                      <span className="flex-1 min-w-[180px] text-sm font-medium">{h.title}</span>
                      <select value={h.status} onChange={e => patchHyp(h.id, { status: e.target.value })} className="text-[11px] h-6 rounded border px-1 bg-transparent" data-testid={`pg07-vt-status-${h.hypothesis_num}`}>
                        {["activa", "pausada", "resuelta"].map(v => <option key={v}>{v}</option>)}
                      </select>
                      <select value={h.criticality} onChange={e => patchHyp(h.id, { criticality: e.target.value })} className="text-[11px] h-6 rounded border px-1 bg-transparent">
                        {["baja", "media", "alta", "crítica"].map(v => <option key={v}>{v}</option>)}
                      </select>
                      <Badge variant="outline" className="text-[10px]">{h.signals_open || 0}/{h.signals_total || 0}</Badge>
                      <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setShowAdd(h.hypothesis_num)} data-testid={`pg07-vt-add-${h.hypothesis_num}`}>
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="text-[10px] text-muted-foreground pl-8">{h.question}</div>
                    {hSigs.map(s => (
                      <div key={s.id} className={`ml-8 mt-1 border-l-2 pl-2 py-0.5 ${s.status === "resuelta" || s.status === "descartada" ? "border-slate-300 opacity-50" : "border-amber-500"}`} data-testid={`pg07-vt-sig-${s.id}`}>
                        <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
                          <span className="font-medium">{s.title}</span>
                          <Badge className={`${VT_SEV_COLOR[s.severity]} text-[9px] h-4 px-1`}>{s.severity}</Badge>
                          <Badge variant="outline" className="text-[9px] h-4 px-1">{s.source}</Badge>
                          <Badge variant="outline" className="text-[9px] h-4 px-1">{s.status}</Badge>
                          {s.status !== "resuelta" && s.status !== "descartada" && (
                            <>
                              <button className="text-[10px] text-emerald-700 hover:underline" onClick={() => closeSig(s.id, "resuelta")}>resolver</button>
                              <button className="text-[10px] text-slate-500 hover:underline" onClick={() => closeSig(s.id, "descartada")}>descartar</button>
                            </>
                          )}
                        </div>
                        {s.description && <div className="text-[10px] text-muted-foreground">{s.description}</div>}
                      </div>
                    ))}
                    {showAdd === h.hypothesis_num && (
                      <div className="ml-8 mt-2 space-y-1.5 border-l-2 border-cyan-500 pl-2" data-testid={`pg07-vt-add-form-${h.hypothesis_num}`}>
                        <input placeholder="Título de la señal" value={newSig.title} onChange={e => setNewSig({ ...newSig, title: e.target.value })} className="w-full h-7 text-xs rounded border px-2 bg-transparent" />
                        <textarea placeholder="Descripción (opcional)" rows={2} value={newSig.description} onChange={e => setNewSig({ ...newSig, description: e.target.value })} className="w-full text-xs rounded border px-2 py-1 bg-transparent" />
                        <div className="flex items-center gap-1.5">
                          <select value={newSig.severity} onChange={e => setNewSig({ ...newSig, severity: e.target.value })} className="h-7 text-xs rounded border px-1.5 bg-transparent">
                            {["info", "media", "alta", "crítica"].map(v => <option key={v}>{v}</option>)}
                          </select>
                          <Button size="sm" className="h-7 text-[10px]" onClick={() => createSig(h.hypothesis_num)} data-testid={`pg07-vt-save-${h.hypothesis_num}`}>Crear</Button>
                          <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => setShowAdd(null)}>Cancelar</Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ApprovalCard({ title, name, role, signed, signedAt, signedBy, onSign }) {
  return (
    <Card className={signed ? "border-emerald-300" : ""}>
      <CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{title}</div>
        <div className="text-lg font-bold mt-1">{name}</div>
        {signed ? (
          <>
            <Badge className="mt-2 bg-emerald-100 text-emerald-700 border-emerald-300">Firmado ✓</Badge>
            <div className="text-xs text-muted-foreground mt-1">{signedBy} · {signedAt?.slice(0, 10)}</div>
          </>
        ) : (
          <Button size="sm" className="mt-3" onClick={onSign} data-testid={`pg07-sign-${role}`}>
            <Signature className="w-3.5 h-3.5 mr-1.5" />Firmar
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────
export default function PG07Page() {
  const { token, user } = useAuth();
  const authH = { Authorization: `Bearer ${token}` };
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [compliance, setCompliance] = useState(null);
  const [phaseCounts, setPhaseCounts] = useState({});
  const [filterPhase, setFilterPhase] = useState("all");
  // Historial PG-07 sin evaluar (petición 2026-08-24): permite llegar aquí ya
  // filtrado desde el aviso ámbar del Panel (/pg07?status=sin_evaluar).
  const [filterStatus, setFilterStatus] = useState(() => searchParams.get("status") || "all");
  const [filterManaged, setFilterManaged] = useState(false);
  const [selected, setSelected] = useState(null);
  const [importing, setImporting] = useState(false);
  const [outerTab, setOuterTab] = useState("matrix");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterManaged ? "?only_managed=true" : "";
      const [c, p] = await Promise.all([
        fetch(`${API}/pg07/compliance${params}`, { headers: authH }).then(r => r.json()),
        fetch(`${API}/pg07/phases`, { headers: authH }).then(r => r.json()),
      ]);
      setCompliance(c);
      setPhaseCounts(p.counts || {});
    } catch { toast.error("Error cargando compliance PG-07"); }
    finally { setLoading(false); }
  }, [filterManaged]); // eslint-disable-line

  useEffect(() => { if (token) load(); }, [token, load]);

  const handleImport = async () => {
    if (!isAdmin) { toast.error("Solo administradores"); return; }
    setImporting(true);
    try {
      const r = await fetch(`${API}/pg07/import-airtable`, { method: "POST", headers: authH });
      const j = await r.json();
      if (r.ok) { toast.success(`Importados ${j.stats.inserted} nuevos, ${j.stats.updated} actualizados`); await load(); }
      else toast.error(j.detail || "Error importando");
    } catch { toast.error("Error importando"); }
    finally { setImporting(false); }
  };

  const rows = (compliance?.rows || []).filter(r => {
    if (filterPhase !== "all" && r.phase !== filterPhase) return false;
    if (filterStatus !== "all" && r.global_status !== filterStatus) return false;
    return true;
  });

  if (loading) return <div className="p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="pg07-page">
      <div className="rounded-2xl border bg-gradient-to-br from-slate-50 via-white to-emerald-50/40 dark:from-slate-950 dark:via-slate-900 dark:to-emerald-950/30 p-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold mb-1 flex items-center gap-1.5">
              <Award className="w-3 h-3" /> Innovation OS · Cumplimiento
            </div>
            <h1 className="text-3xl font-bold tracking-tight">PG-07 · Gestión y desarrollo de proyectos</h1>
            <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
              Alineado con <b>ISO 56001:2024</b>. Fichas F.01 Riesgos, F.02 Colaboradores, seguimiento trimestral y detección auto desviación {'>'}10%.
            </p>
            <p className="text-xs text-muted-foreground mt-1" data-testid="pg07-vs-auren-note">
              El % de cumplimiento aquí lo definen F.01 + F.02 (+ ficha de cierre si el proyecto ya
              cerró) — el seguimiento trimestral y la aprobación se muestran por proyecto pero no
              anulan el % de cabecera. No es el mismo indicador que el "% Checklist ISO 56001" de{" "}
              <button className="underline hover:text-foreground" onClick={() => navigate("/auren")} data-testid="pg07-goto-auren-link">/auren</button>.
            </p>
          </div>
          {isAdmin && (
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={async () => {
                const r = await fetch(`${API}/fireflies/sync?limit=50`, { method: "POST", headers: authH });
                const j = await r.json();
                if (r.ok) toast.success(`Fireflies: ${j.inserted} nuevas, ${j.updated} actualizadas, ${j.auto_linked} auto-vinculadas`);
                else toast.error("Error sincronizando Fireflies");
              }} data-testid="pg07-fireflies-sync"><RefreshCw className="w-4 h-4 mr-2" />Sync Fireflies</Button>
              <Button variant="outline" onClick={handleImport} disabled={importing} data-testid="pg07-import-btn">
                {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Sincronizar desde Airtable
              </Button>
            </div>
          )}
        </div>
        <div className="mt-6"><ComplianceKpis counts={compliance?.counts || {}} total={compliance?.total || 0} /></div>
      </div>

      <Tabs value={outerTab} onValueChange={setOuterTab}>
        <TabsList>
          <TabsTrigger value="matrix" data-testid="pg07-outer-matrix"><ShieldCheck className="w-3.5 h-3.5 mr-1.5" />Matriz cumplimiento</TabsTrigger>
          <TabsTrigger value="ranking" data-testid="pg07-outer-ranking"><TrendingUp className="w-3.5 h-3.5 mr-1.5" />Ranking PM</TabsTrigger>
          <TabsTrigger value="kanban" data-testid="pg07-outer-kanban"><Users2 className="w-3.5 h-3.5 mr-1.5" />Kanban PMs</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="pg07-outer-invoices"><Receipt className="w-3.5 h-3.5 mr-1.5" />Facturas</TabsTrigger>
        </TabsList>

        <TabsContent value="matrix" className="mt-4 space-y-4">
          <div className="flex items-center flex-wrap gap-3">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Filtros:</div>
            <Select value={filterPhase} onValueChange={setFilterPhase}>
              <SelectTrigger className="w-auto min-w-[170px]" data-testid="pg07-filter-phase"><SelectValue placeholder="Fase" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las fases</SelectItem>
                {Object.entries(phaseCounts).map(([ph, c]) => <SelectItem key={ph} value={ph}>{ph} ({c})</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-auto min-w-[150px]" data-testid="pg07-filter-status"><SelectValue placeholder="Estado" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="red">🔴 No conforme</SelectItem>
                <SelectItem value="amber">🟠 En riesgo</SelectItem>
                <SelectItem value="ok">🟢 OK</SelectItem>
                <SelectItem value="sin_evaluar">⚫ Sin evaluar</SelectItem>
                <SelectItem value="na">⚪ No aplica</SelectItem>
              </SelectContent>
            </Select>
            <Button variant={filterManaged ? "default" : "outline"} size="sm" onClick={() => setFilterManaged(v => !v)} data-testid="pg07-filter-managed">
              <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />{filterManaged ? "Solo PG-07 formal ✓" : "Todos"}
            </Button>
            <div className="ml-auto text-xs text-muted-foreground" data-testid="pg07-count">
              {rows.length} de {compliance?.total || 0} proyectos · Año {compliance?.year} · {compliance?.quarter}
            </div>
          </div>
          <ComplianceMatrix rows={rows} onSelect={(r) => setSelected(r.project_id)} />
          <Card className="bg-muted/30">
            <CardContent className="p-4 text-xs text-muted-foreground grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="flex items-center gap-2"><StatusDot status="ok" size="w-2.5 h-2.5" /> Ficha al día</div>
              <div className="flex items-center gap-2"><StatusDot status="amber" size="w-2.5 h-2.5" /> Registrada pero incompleta</div>
              <div className="flex items-center gap-2"><StatusDot status="red" size="w-2.5 h-2.5" /> Falta / vencida</div>
              <div className="flex items-center gap-2"><StatusDot status="na" size="w-2.5 h-2.5" /> No aplica a la fase actual</div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ranking" className="mt-4">
          <PMRankingView authH={authH} onSelectProject={(pid) => setSelected(pid)} />
        </TabsContent>

        <TabsContent value="kanban" className="mt-4">
          <PMKanbanView authH={authH} onSelectProject={(pid) => setSelected(pid)} />
        </TabsContent>

        <TabsContent value="invoices" className="mt-4">
          <PG07InvoicesPanel authH={authH} />
        </TabsContent>
      </Tabs>

      <ProjectDetailDialog
        open={!!selected}
        onOpenChange={(v) => !v && setSelected(null)}
        projectId={selected}
        authH={authH}
        onDataChanged={load}
      />
    </div>
  );
}
